import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { 
  ArrowLeft, Zap, DollarSign, ChevronDown, ChevronUp, 
  Filter, Calendar, User, Cpu, RefreshCw, X, Copy, Check, ExternalLink, CheckSquare, Square, Database, Coins, History
} from "lucide-react";
import { formatInTimezone } from "../utils/timezone";
import AdminMenu from "../components/AdminMenu";

const PromptLogTokens = () => {
  const navigate = useNavigate();
  const { userId: urlUserId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [totals, setTotals] = useState({
    input_tokens: 0,
    output_tokens: 0,
    total_tokens: 0,
    total_cost: 0,
    total_count: 0,
    total_crc_charged: 0
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 50,
    has_more: false,
    total_count: 0
  });
  const [availableModels, setAvailableModels] = useState([]);
  const [showTotalsDropdown, setShowTotalsDropdown] = useState(false);
  
  // Selection state
  const [selectedLogIds, setSelectedLogIds] = useState(new Set());
  const [showSelectedDropdown, setShowSelectedDropdown] = useState(false);
  
  // Modal state
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [fullPromptData, setFullPromptData] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Cost to Credit state
  const [c2cValue, setC2cValue] = useState(null);
  const [usdCrcValue, setUsdCrcValue] = useState(null);
  const [showC2cLogModal, setShowC2cLogModal] = useState(false);
  const [c2cLogs, setC2cLogs] = useState([]);
  const [c2cLogPagination, setC2cLogPagination] = useState({ skip: 0, limit: 20, total_count: 0, has_more: false });
  const [c2cLogLoading, setC2cLogLoading] = useState(false);
  
  // Filter states
  const [selectedModel, setSelectedModel] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [userId, setUserId] = useState(urlUserId || "");
  const [costOnly, setCostOnly] = useState(false);
  
  // Applied filters (to track what's currently showing)
  const [appliedFilters, setAppliedFilters] = useState({
    model: "",
    fromDate: "",
    toDate: "",
    userId: urlUserId || "",
    costOnly: false
  });

  // Get user timezone
  const userTimezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const fetchTokens = useCallback(async (skip = 0, filters = appliedFilters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("skip", skip);
      params.append("limit", 50);
      
      if (filters.model) params.append("model", filters.model);
      if (filters.fromDate) params.append("from_date", new Date(filters.fromDate).toISOString());
      if (filters.toDate) params.append("to_date", new Date(filters.toDate).toISOString());
      if (filters.userId) params.append("user_id", filters.userId);
      if (filters.costOnly) params.append("cost_only", "true");
      
      const response = await authAxios.get(`/admin/plog/tokens?${params.toString()}`);
      
      setLogs(response.data.logs || []);
      setTotals(response.data.totals || {});
      setPagination(response.data.pagination || {});
      setAvailableModels(response.data.available_models || []);
    } catch (error) {
      console.error("Failed to fetch tokens:", error);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    // If URL has userId, fetch with that filter applied
    if (urlUserId) {
      const initialFilters = { ...appliedFilters, userId: urlUserId };
      setAppliedFilters(initialFilters);
      fetchTokens(0, initialFilters);
    } else {
      fetchTokens(0);
    }
    fetchC2cValue();
  }, [urlUserId]);

  // Fetch current C2C value
  const fetchC2cValue = async () => {
    try {
      const response = await authAxios.get('/admin/plog/c2c');
      setC2cValue(response.data.c2c_value);
      setUsdCrcValue(response.data.usd_crc);
    } catch (error) {
      console.error("Failed to fetch C2C value:", error);
    }
  };

  // Fetch C2C change logs
  const fetchC2cLogs = async (skip = 0) => {
    setC2cLogLoading(true);
    try {
      const response = await authAxios.get(`/admin/plog/c2c/log?skip=${skip}&limit=20`);
      setC2cLogs(response.data.logs || []);
      setC2cLogPagination(response.data.pagination || {});
    } catch (error) {
      console.error("Failed to fetch C2C logs:", error);
    } finally {
      setC2cLogLoading(false);
    }
  };

  // Open C2C log modal
  const openC2cLogModal = () => {
    setShowC2cLogModal(true);
    fetchC2cLogs(0);
  };

  const handleApplyFilters = () => {
    const newFilters = {
      model: selectedModel,
      fromDate: fromDate,
      toDate: toDate,
      userId: userId,
      costOnly: costOnly
    };
    setAppliedFilters(newFilters);
    fetchTokens(0, newFilters);
  };

  const handleClearFilters = () => {
    setSelectedModel("");
    setFromDate("");
    setToDate("");
    setUserId("");
    setCostOnly(false);
    const emptyFilters = { model: "", fromDate: "", toDate: "", userId: "", costOnly: false };
    setAppliedFilters(emptyFilters);
    fetchTokens(0, emptyFilters);
  };

  const handlePageChange = (newSkip) => {
    fetchTokens(newSkip, appliedFilters);
  };

  // Handle row click - open modal with full prompt data
  const handleRowClick = async (log) => {
    setSelectedLog(log);
    setModalLoading(true);
    setFullPromptData(null);
    
    try {
      // Fetch full prompt data from session
      const response = await authAxios.get(`/admin/plog/session/${log.session_id}?limit=1000&skip=0`);
      const prompts = response.data.prompts || [];
      // Find the specific prompt by log_id
      const fullPrompt = prompts.find(p => p.log_id === log.log_id);
      setFullPromptData(fullPrompt || log);
    } catch (error) {
      console.error("Failed to fetch prompt details:", error);
      setFullPromptData(log); // Fall back to summary data
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedLog(null);
    setFullPromptData(null);
    setCopied(false);
  };

  const copyContent = () => {
    const content = fullPromptData?.content || "";
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openFullPage = () => {
    if (selectedLog) {
      navigate(`/plog/prompt/${selectedLog.session_id}/${selectedLog.log_id}`);
    }
  };

  const getPromptTypeColor = (promptType) => {
    switch (promptType) {
      case "assistant":
        return "bg-blue-500/20 border-blue-500/40 text-blue-300";
      case "user":
        return "bg-green-500/20 border-green-500/40 text-green-300";
      case "system":
        return "bg-purple-500/20 border-purple-500/40 text-purple-300";
      default:
        return "bg-white/10 border-white/20 text-white/70";
    }
  };

  const formatCost = (cost) => {
    if (cost < 0.01) return `$${cost.toFixed(6)}`;
    if (cost < 1) return `$${cost.toFixed(4)}`;
    return `$${cost.toFixed(2)}`;
  };

  // Compute selected totals
  const selectedTotals = useMemo(() => {
    if (selectedLogIds.size === 0) {
      return { input_tokens: 0, output_tokens: 0, total_tokens: 0, total_cost: 0, count: 0, cache_creation_tokens: 0, cache_read_tokens: 0, crc_charged: 0 };
    }
    
    const selectedLogs = logs.filter(log => selectedLogIds.has(log.log_id));
    return selectedLogs.reduce((acc, log) => ({
      input_tokens: acc.input_tokens + (log.input_tokens || 0),
      output_tokens: acc.output_tokens + (log.output_tokens || 0),
      total_tokens: acc.total_tokens + (log.total_tokens || 0),
      total_cost: acc.total_cost + (log.estimated_cost || 0),
      count: acc.count + 1,
      cache_creation_tokens: acc.cache_creation_tokens + (log.cache_creation_tokens || 0),
      cache_read_tokens: acc.cache_read_tokens + (log.cache_read_tokens || 0),
      crc_charged: acc.crc_charged + (log.crc_charged || 0)
    }), { input_tokens: 0, output_tokens: 0, total_tokens: 0, total_cost: 0, count: 0, cache_creation_tokens: 0, cache_read_tokens: 0, crc_charged: 0 });
  }, [selectedLogIds, logs]);

  // Toggle selection for a log
  const toggleLogSelection = (logId, e) => {
    e.stopPropagation(); // Prevent row click (modal open)
    setSelectedLogIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  // Select/deselect all visible logs
  const toggleSelectAll = () => {
    if (selectedLogIds.size === logs.length) {
      setSelectedLogIds(new Set());
    } else {
      setSelectedLogIds(new Set(logs.map(log => log.log_id)));
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedLogIds(new Set());
  };

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total_count / pagination.limit);

  return (
    <div className="min-h-screen bg-divine-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-divine-black/95 backdrop-blur border-b border-divine-gold/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back link and title */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/plog")}
                className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Prompt Log</span>
              </button>
              <h1 className="font-cinzel text-xl text-divine-gold">Token Usage</h1>
            </div>
            
            {/* Right: Totals display */}
            <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowTotalsDropdown(!showTotalsDropdown)}
                className="flex items-center gap-4 px-4 py-2 bg-white/5 rounded-lg border border-divine-gold/30 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-divine-gold" />
                  <span className="text-white font-medium">
                    {totals.total_tokens?.toLocaleString() || 0}
                  </span>
                  <span className="text-white/40 text-sm">tokens</span>
                </div>
                <div className="w-px h-6 bg-white/20" />
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-400" />
                  <span className="text-white font-medium">
                    {formatCost(totals.total_cost || 0)}
                  </span>
                </div>
                {showTotalsDropdown ? (
                  <ChevronUp className="w-4 h-4 text-white/40" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-white/40" />
                )}
              </button>
              
              {/* Totals Dropdown */}
              {showTotalsDropdown && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-divine-charcoal border border-divine-gold/30 rounded-lg shadow-xl z-50 p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-white/60">Total Logs</span>
                      <span className="text-white font-medium">{totals.total_count?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Input Tokens</span>
                      <span className="text-white font-medium">{totals.input_tokens?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Output Tokens</span>
                      <span className="text-white font-medium">{totals.output_tokens?.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-white/10 pt-3">
                      <div className="flex justify-between">
                        <span className="text-white/60">Total Tokens</span>
                        <span className="text-divine-gold font-medium">{totals.total_tokens?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between mt-2">
                        <span className="text-white/60">Total Cost</span>
                        <span className="text-green-400 font-medium">{formatCost(totals.total_cost || 0)}</span>
                      </div>
                      <div className="flex justify-between mt-2">
                        <span className="text-white/60">Total CRC</span>
                        <span className="text-amber-400 font-medium">{totals.total_crc_charged?.toLocaleString() || 0}</span>
                      </div>
                    </div>
                    {/* Cache Metrics */}
                    {(totals.cache_creation_tokens > 0 || totals.cache_read_tokens > 0) && (
                      <div className="border-t border-white/10 pt-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Database className="w-3 h-3 text-cyan-400" />
                          <span className="text-cyan-400 text-sm font-medium">Anthropic Cache</span>
                        </div>
                        {totals.cache_creation_tokens > 0 && (
                          <div className="flex justify-between">
                            <span className="text-white/60 text-sm">Cache Write</span>
                            <span className="text-orange-400 font-medium text-sm">{totals.cache_creation_tokens?.toLocaleString()}</span>
                          </div>
                        )}
                        {totals.cache_read_tokens > 0 && (
                          <div className="flex justify-between mt-1">
                            <span className="text-white/60 text-sm">Cache Read (90% saved)</span>
                            <span className="text-cyan-400 font-medium text-sm">{totals.cache_read_tokens?.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Selected Summary - Only shows when items are selected */}
            {selectedLogIds.size > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowSelectedDropdown(!showSelectedDropdown)}
                  className="flex items-center gap-4 px-4 py-2 bg-blue-500/20 rounded-lg border border-blue-500/40 hover:bg-blue-500/30 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <CheckSquare className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-300 font-medium">
                      {selectedLogIds.size} selected
                    </span>
                  </div>
                  <div className="w-px h-6 bg-blue-500/30" />
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-blue-400" />
                    <span className="text-white font-medium">
                      {selectedTotals.total_tokens?.toLocaleString() || 0}
                    </span>
                  </div>
                  <div className="w-px h-6 bg-blue-500/30" />
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-400" />
                    <span className="text-white font-medium">
                      {formatCost(selectedTotals.total_cost || 0)}
                    </span>
                  </div>
                  {showSelectedDropdown ? (
                    <ChevronUp className="w-4 h-4 text-blue-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-blue-400" />
                  )}
                </button>
                
                {/* Selected Dropdown */}
                {showSelectedDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-divine-charcoal border border-blue-500/30 rounded-lg shadow-xl z-50 p-4">
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/10">
                      <span className="text-blue-300 font-medium flex items-center gap-2">
                        <CheckSquare className="w-4 h-4" />
                        Selected Logs
                      </span>
                      <button
                        onClick={clearSelection}
                        className="text-xs text-white/40 hover:text-white/60 transition-colors"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-white/60">Selected Logs</span>
                        <span className="text-blue-300 font-medium">{selectedTotals.count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">Input Tokens</span>
                        <span className="text-white font-medium">{selectedTotals.input_tokens?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">Output Tokens</span>
                        <span className="text-white font-medium">{selectedTotals.output_tokens?.toLocaleString()}</span>
                      </div>
                      <div className="border-t border-white/10 pt-3">
                        <div className="flex justify-between">
                          <span className="text-white/60">Total Tokens</span>
                          <span className="text-blue-300 font-medium">{selectedTotals.total_tokens?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between mt-2">
                          <span className="text-white/60">Total Cost</span>
                          <span className="text-green-400 font-medium">{formatCost(selectedTotals.total_cost || 0)}</span>
                        </div>
                        <div className="flex justify-between mt-2">
                          <span className="text-white/60">Total CRC</span>
                          <span className="text-amber-400 font-medium">{selectedTotals.crc_charged?.toLocaleString() || 0}</span>
                        </div>
                      </div>
                      {/* Cache Metrics for Selected */}
                      {(selectedTotals.cache_creation_tokens > 0 || selectedTotals.cache_read_tokens > 0) && (
                        <div className="border-t border-white/10 pt-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Database className="w-3 h-3 text-cyan-400" />
                            <span className="text-cyan-400 text-sm font-medium">Cache</span>
                          </div>
                          {selectedTotals.cache_creation_tokens > 0 && (
                            <div className="flex justify-between">
                              <span className="text-white/60 text-sm">Cache Write</span>
                              <span className="text-orange-400 font-medium text-sm">{selectedTotals.cache_creation_tokens?.toLocaleString()}</span>
                            </div>
                          )}
                          {selectedTotals.cache_read_tokens > 0 && (
                            <div className="flex justify-between mt-1">
                              <span className="text-white/60 text-sm">Cache Read</span>
                              <span className="text-cyan-400 font-medium text-sm">{selectedTotals.cache_read_tokens?.toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <AdminMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Cost to Credit Section */}
      <div className="max-w-7xl mx-auto px-4 pt-4">
        <div className="bg-gradient-to-r from-divine-gold/10 to-amber-500/10 rounded-lg border border-divine-gold/30 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-divine-gold/20 rounded-lg">
                  <Coins className="w-5 h-5 text-divine-gold" />
                </div>
                <div>
                  <span className="text-white/60 text-sm">Credit Multiplier (c2c)</span>
                  <div className="text-2xl font-bold text-divine-gold">
                    {c2cValue !== null ? c2cValue : "—"}
                  </div>
                </div>
              </div>
              <div className="border-l border-divine-gold/30 pl-6">
                <span className="text-white/60 text-sm">USD to CRC</span>
                <div className="text-2xl font-bold text-amber-400">
                  {usdCrcValue !== null ? usdCrcValue : "—"}
                </div>
              </div>
            </div>
            <button
              onClick={openC2cLogModal}
              className="flex items-center gap-2 px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              <History className="w-4 h-4" />
              View Log
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="bg-white/5 rounded-lg border border-white/10 p-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-divine-gold" />
            <span className="text-white/80 font-medium">Filters</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* LLM Model Dropdown */}
            <div>
              <label className="block text-white/60 text-sm mb-1">
                <Cpu className="w-3 h-3 inline mr-1" />
                LLM Model
              </label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full bg-divine-black border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:border-divine-gold/50 focus:outline-none"
              >
                <option value="">All Models</option>
                {/* Provider Groups */}
                <option value="anthropic_all_claude" className="font-semibold">🟣 Anthropic ALL Claude</option>
                <option value="openai_all" className="font-semibold">🟢 OpenAI ALL</option>
                <option value="google_all_gemini" className="font-semibold">🔵 Google ALL Gemini</option>
                <option disabled>──────────────</option>
                {/* Individual Models */}
                {availableModels.map((model) => (
                  <option key={model} value={model}>{model}</option>
                ))}
              </select>
            </div>
            
            {/* From Date */}
            <div>
              <label className="block text-white/60 text-sm mb-1">
                <Calendar className="w-3 h-3 inline mr-1" />
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full bg-divine-black border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:border-divine-gold/50 focus:outline-none"
              />
            </div>
            
            {/* To Date */}
            <div>
              <label className="block text-white/60 text-sm mb-1">
                <Calendar className="w-3 h-3 inline mr-1" />
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full bg-divine-black border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:border-divine-gold/50 focus:outline-none"
              />
            </div>
            
            {/* User ID */}
            <div>
              <label className="block text-white/60 text-sm mb-1">
                <User className="w-3 h-3 inline mr-1" />
                User ID
              </label>
              <input
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="user_xxxxxxxxxx"
                className="w-full bg-divine-black border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:border-divine-gold/50 focus:outline-none placeholder:text-white/30"
              />
            </div>
          </div>
          
          {/* Filter Actions */}
          <div className="flex items-center gap-3 mt-4">
            {/* Cost Only Toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <div 
                onClick={() => setCostOnly(!costOnly)}
                className={`relative w-10 h-5 rounded-full transition-colors ${costOnly ? 'bg-green-500' : 'bg-white/20'}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${costOnly ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
              <span className="text-white/80 text-sm">Cost Only</span>
            </label>
            
            <div className="w-px h-6 bg-white/20" />
            
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-divine-gold text-divine-black font-medium rounded-lg hover:bg-divine-gold/90 transition-colors"
            >
              Apply Filters
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              Clear
            </button>
            <button
              onClick={() => fetchTokens(pagination.skip, appliedFilters)}
              className="p-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Log List */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[2rem_1.5fr_1fr_1fr_0.7fr_1.2fr_0.8fr_0.7fr_0.6fr] gap-4 px-4 py-3 bg-white/5 border-b border-white/10 text-white/60 text-sm font-medium">
            <div className="flex items-center">
              <button
                onClick={toggleSelectAll}
                className="p-1 hover:bg-white/10 rounded transition-colors"
                title={selectedLogIds.size === logs.length ? "Deselect all" : "Select all"}
              >
                {selectedLogIds.size === logs.length && logs.length > 0 ? (
                  <CheckSquare className="w-4 h-4 text-blue-400" />
                ) : (
                  <Square className="w-4 h-4 text-white/40" />
                )}
              </button>
            </div>
            <div>LLM</div>
            <div>User</div>
            <div>Date & Time</div>
            <div>Type</div>
            <div>Agent</div>
            <div className="text-right">Tokens</div>
            <div className="text-right">Cost</div>
            <div className="text-right">CRC</div>
          </div>
          
          {/* Table Body */}
          {loading ? (
            <div className="p-8 text-center text-white/40">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading...
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-white/40">
              No logs found matching filters
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {logs.map((log, index) => (
                <div
                  key={log.log_id || index}
                  onClick={() => handleRowClick(log)}
                  className={`grid grid-cols-[2rem_1.5fr_1fr_1fr_0.7fr_1.2fr_0.8fr_0.7fr_0.6fr] gap-4 px-4 py-3 hover:bg-white/10 transition-colors border-l-4 cursor-pointer ${getPromptTypeColor(log.prompt_type)} ${selectedLogIds.has(log.log_id) ? 'bg-blue-500/10' : ''}`}
                >
                  <div className="flex items-center">
                    <button
                      onClick={(e) => toggleLogSelection(log.log_id, e)}
                      className="p-1 hover:bg-white/10 rounded transition-colors"
                    >
                      {selectedLogIds.has(log.log_id) ? (
                        <CheckSquare className="w-4 h-4 text-blue-400" />
                      ) : (
                        <Square className="w-4 h-4 text-white/40" />
                      )}
                    </button>
                  </div>
                  <div className="text-sm">
                    <div className="text-white/80 truncate" title={log.model}>
                      {log.model || "unknown"}
                    </div>
                    {log.request_id && (
                      <div className="text-white/30 text-xs truncate font-mono" title={log.request_id}>
                        {log.request_id}
                      </div>
                    )}
                  </div>
                  <div className="text-sm">
                    <div className="text-white/80 truncate">{log.user_name || "Unknown"}</div>
                    <div className="text-white/40 text-xs truncate">{log.user_id || "N/A"}</div>
                  </div>
                  <div className="text-white/60 text-sm">
                    {log.created_at ? formatInTimezone(log.created_at, userTimezone) : "N/A"}
                  </div>
                  <div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      log.prompt_type === "assistant" ? "bg-blue-500/20 text-blue-300" :
                      log.prompt_type === "user" ? "bg-green-500/20 text-green-300" :
                      log.prompt_type === "system" ? "bg-purple-500/20 text-purple-300" :
                      "bg-white/10 text-white/60"
                    }`}>
                      {log.prompt_type || "unknown"}
                    </span>
                  </div>
                  <div className="text-sm">
                    <div className="text-white/60 truncate" title={log.agent_name}>
                      {log.agent_name || "-"}
                    </div>
                    <div className="text-white/30 text-xs truncate font-mono" title={log.log_id}>
                      {log.log_id}
                    </div>
                  </div>
                  <div className="text-right text-white/80 text-sm">
                    <div>{log.total_tokens?.toLocaleString() || 0}</div>
                    {/* Cache indicators */}
                    {(log.cache_creation_tokens || log.cache_read_tokens) && (
                      <div className="flex items-center justify-end gap-1 mt-0.5">
                        <Database className="w-3 h-3 text-cyan-400" />
                        {log.cache_creation_tokens > 0 && (
                          <span className="text-orange-400 text-xs" title="Cache Write">
                            W:{log.cache_creation_tokens.toLocaleString()}
                          </span>
                        )}
                        {log.cache_read_tokens > 0 && (
                          <span className="text-cyan-400 text-xs" title="Cache Read (90% saved)">
                            R:{log.cache_read_tokens.toLocaleString()}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-green-400 text-sm">
                    {formatCost(log.estimated_cost || 0)}
                  </div>
                  <div className="text-right text-sm">
                    {log.crc_charged !== null && log.crc_charged !== undefined ? (
                      <span className="text-amber-400 font-medium">{log.crc_charged.toLocaleString()}</span>
                    ) : (
                      <span className="text-white/30">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {!loading && logs.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 bg-white/5 border-t border-white/10">
              <div className="text-white/60 text-sm">
                Showing {pagination.skip + 1} - {pagination.skip + logs.length} of {pagination.total_count?.toLocaleString()} logs
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(Math.max(0, pagination.skip - pagination.limit))}
                  disabled={pagination.skip === 0}
                  className="px-3 py-1 bg-white/10 text-white rounded hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-white/60 text-sm px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.skip + pagination.limit)}
                  disabled={!pagination.has_more}
                  className="px-3 py-1 bg-white/10 text-white rounded hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Prompt Preview Modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={closeModal}>
          <div 
            className="bg-divine-charcoal border border-divine-gold/30 rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col m-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedLog.prompt_type === "assistant" ? "bg-blue-500/20 text-blue-300" :
                  selectedLog.prompt_type === "user" ? "bg-green-500/20 text-green-300" :
                  selectedLog.prompt_type === "system" ? "bg-purple-500/20 text-purple-300" :
                  "bg-white/10 text-white/60"
                }`}>
                  {selectedLog.prompt_type}
                </span>
                <span className="text-white/60 text-sm">{selectedLog.agent_name || "unknown agent"}</span>
                <span className="text-white/40 text-sm">•</span>
                <span className="text-white/60 text-sm">{selectedLog.model}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={copyContent}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Copy content"
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-white/60" />}
                </button>
                <button
                  onClick={openFullPage}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Open full page"
                >
                  <ExternalLink className="w-4 h-4 text-white/60" />
                </button>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white/60" />
                </button>
              </div>
            </div>
            
            {/* Modal Metadata */}
            <div className="px-6 py-3 bg-white/5 border-b border-white/10 flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-white/40" />
                <span className="text-white/60">{selectedLog.user_name || "Unknown"}</span>
                <span className="text-white/30 text-xs">({selectedLog.user_id})</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-divine-gold" />
                <span className="text-white/80">{selectedLog.total_tokens?.toLocaleString()} tokens</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-green-400" />
                <span className="text-green-400">{formatCost(selectedLog.estimated_cost || 0)}</span>
              </div>
              <div className="text-white/40">
                {selectedLog.created_at ? formatInTimezone(selectedLog.created_at, userTimezone) : ""}
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-6">
              {modalLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-divine-gold" />
                </div>
              ) : (
                <pre className="whitespace-pre-wrap text-white/80 text-sm font-mono leading-relaxed">
                  {fullPromptData?.content || "No content available"}
                </pre>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-3 border-t border-white/10 flex items-center justify-between text-xs text-white/40 font-mono">
              <div className="flex items-center gap-4">
                <span>Log: {selectedLog.log_id?.slice(0, 8)}...</span>
                {(selectedLog.request_id || fullPromptData?.request_id) && (
                  <span>Request: {selectedLog.request_id || fullPromptData?.request_id}</span>
                )}
              </div>
              <button 
                onClick={() => {
                  closeModal();
                  navigate(`/plog?session=${selectedLog.session_id}`);
                }}
                className="hover:text-divine-gold hover:underline transition-colors cursor-pointer"
                title="View session in Prompt Log"
              >
                Session: {selectedLog.session_id}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* C2C Log Modal */}
      {showC2cLogModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-divine-charcoal border border-divine-gold/30 rounded-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <History className="w-5 h-5 text-divine-gold" />
                <h2 className="text-lg font-semibold text-white">Cost to Credit History</h2>
              </div>
              <button
                onClick={() => setShowC2cLogModal(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-4">
              {c2cLogLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 text-divine-gold animate-spin" />
                </div>
              ) : c2cLogs.length === 0 ? (
                <div className="text-center py-12 text-white/40">
                  No history available
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-white/60 text-sm border-b border-white/10">
                      <th className="pb-3 pr-4">C2C Value</th>
                      <th className="pb-3 pr-4">USD to CRC</th>
                      <th className="pb-3 pr-4">User</th>
                      <th className="pb-3 pr-4">IP Address</th>
                      <th className="pb-3">Date & Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {c2cLogs.map((log, idx) => (
                      <tr key={idx} className="text-sm">
                        <td className="py-3 pr-4">
                          <span className="text-divine-gold font-bold text-lg">{log.c2c_value}</span>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="text-amber-400 font-bold">{log.usd_crc !== null && log.usd_crc !== undefined ? log.usd_crc : "—"}</span>
                        </td>
                        <td className="py-3 pr-4">
                          <div className="text-white">{log.user_name || "Unknown"}</div>
                          <div className="text-white/40 text-xs">{log.user_id}</div>
                        </td>
                        <td className="py-3 pr-4 text-white/60 font-mono text-xs">
                          {log.ip_address || "—"}
                        </td>
                        <td className="py-3 text-white/60">
                          {log.created_at ? formatInTimezone(log.created_at, user?.timezone || "UTC") : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            
            {/* Modal Footer - Pagination */}
            {c2cLogPagination.total_count > 0 && (
              <div className="flex items-center justify-between p-4 border-t border-white/10">
                <span className="text-white/60 text-sm">
                  Showing {c2cLogPagination.skip + 1}-{Math.min(c2cLogPagination.skip + c2cLogs.length, c2cLogPagination.total_count)} of {c2cLogPagination.total_count}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchC2cLogs(Math.max(0, c2cLogPagination.skip - 20))}
                    disabled={c2cLogPagination.skip === 0}
                    className="px-3 py-1 bg-white/10 text-white rounded hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ← Prev
                  </button>
                  <button
                    onClick={() => fetchC2cLogs(c2cLogPagination.skip + 20)}
                    disabled={!c2cLogPagination.has_more}
                    className="px-3 py-1 bg-white/10 text-white rounded hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptLogTokens;
